import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { ModuleKey } from '@gty/common';

import { usePrefetchModule } from '../app/usePrefetch';

import {
  CUSTOMER_PORTAL_ENTRY,
  MODULE_REGISTRY,
  NAV_GROUPS,
  PENDING_NAV,
  type BadgeKey,
  type ModuleRegistryEntry,
  type NavGroup,
  type PendingNavEntry,
} from '../app/module-registry';
import { usePermissions } from '../auth/usePermissions';
import { cn } from '../ui/cn';
import { Icon } from '../ui/icons';

export interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  badges: Partial<Record<BadgeKey, number>>;
  /** Drawer mode: always expanded, closes on navigation. */
  asDrawer?: boolean;
  onNavigate?: () => void;
}

/**
 * Nav item colours.
 *
 * The design computes these in prototype state (`nb.*` / `nf.*`), so the exact
 * values are not in the markup. Active is the design's primary treatment —
 * the 90° brand gradient used by every other primary control in the file.
 * Inactive hover uses a neutral wash rather than the design's
 * `filter:brightness(.97)`, which does nothing on a transparent background.
 */
const ITEM_BASE =
  'group relative flex w-full items-center gap-[11px] rounded-[7px] text-left text-[13.5px] font-semibold leading-none transition-[padding,background-color,color] duration-200';
const ITEM_ACTIVE = 'bg-brand-gradient text-white';
const ITEM_IDLE = 'text-neutral-700 hover:bg-neutral-100 hover:text-ink';

function Badge({ count, active }: { count: number; active: boolean }): JSX.Element {
  return (
    <span
      className={cn(
        'rounded-[20px] px-1.5 py-[3px] text-[10.5px] font-bold leading-none',
        active ? 'bg-white/22 text-white' : 'bg-neutral-200 text-neutral-700',
      )}
    >
      {count}
    </span>
  );
}

function NavItem({
  entry,
  collapsed,
  badge,
  onNavigate,
  onTip,
  onTipHide,
  onPrefetch,
}: {
  entry: ModuleRegistryEntry;
  collapsed: boolean;
  badge?: number;
  onNavigate?: () => void;
  onTip: (label: string, top: number) => void;
  onTipHide: () => void;
  onPrefetch: (module: ModuleKey) => void;
}): JSX.Element {
  return (
    <NavLink
      to={entry.path}
      onClick={onNavigate}
      /*
       * HOVER IS THE EARLIEST HONEST SIGNAL OF INTENT.
       *
       * The gap between a pointer landing on a nav item and the click is
       * typically 200-400ms — dead time the app used to spend doing nothing and
       * then spend again, visibly, after the click. Starting the fetch and the
       * lazy route chunk here means the screen is frequently already warm by
       * the time the route changes.
       *
       * `onFocus` as well as `onMouseEnter`, so keyboard users tabbing through
       * the nav get the same head start rather than only mouse users.
       */
      onMouseEnter={(e) => {
        onPrefetch(entry.module);
        if (collapsed) onTip(entry.label, e.currentTarget.getBoundingClientRect().top + 18);
      }}
      onFocus={() => {
        onPrefetch(entry.module);
      }}
      onMouseLeave={onTipHide}
      className={({ isActive }) =>
        cn(
          ITEM_BASE,
          collapsed ? 'justify-center px-0 py-2.5' : 'px-[11px] py-2.5',
          isActive ? ITEM_ACTIVE : ITEM_IDLE,
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon name={entry.icon} size={17} className="flex-none" />
          {!collapsed && (
            <>
              <span className="flex-1 whitespace-nowrap">{entry.label}</span>
              {badge !== undefined && badge > 0 && <Badge count={badge} active={isActive} />}
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

/**
 * A design nav item with no `ModuleKey` behind it.
 *
 * Rendered so the sidebar matches the mockup, but non-interactive: there is no
 * permission to check it against, and routing to an unguarded screen would be
 * exactly the frontend-only access control the spec forbids.
 */
function PendingItem({
  entry,
  collapsed,
  onTip,
  onTipHide,
}: {
  entry: PendingNavEntry;
  collapsed: boolean;
  onTip: (label: string, top: number) => void;
  onTipHide: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled
      title={entry.reason}
      onMouseEnter={(e) => {
        if (collapsed) onTip(entry.label, e.currentTarget.getBoundingClientRect().top + 18);
      }}
      onMouseLeave={onTipHide}
      className={cn(
        ITEM_BASE,
        collapsed ? 'justify-center px-0 py-2.5' : 'px-[11px] py-2.5',
        'cursor-not-allowed text-neutral-400',
      )}
    >
      <Icon name={entry.icon} size={17} className="flex-none" />
      {!collapsed && (
        <>
          <span className="flex-1 whitespace-nowrap">{entry.label}</span>
          <span className="text-[9.5px] font-bold uppercase tracking-caps text-neutral-400">soon</span>
        </>
      )}
    </button>
  );
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  badges,
  asDrawer = false,
  onNavigate,
}: SidebarProps): JSX.Element {
  const { allows } = usePermissions();
  const prefetchModule = usePrefetchModule();
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);

  // A drawer is never a rail — collapsing it would leave a 72px panel floating
  // over a scrim, which is neither.
  const isCollapsed = asDrawer ? false : collapsed;

  const showTip = (label: string, top: number): void => setTip({ label, top });
  const hideTip = (): void => setTip(null);

  /**
   * Nav is derived from the SAME registry and permission map the routes use, so
   * a rendered link always leads somewhere the user can enter. `allows` ranks
   * the levels via the shared helper — never compare the strings directly,
   * 'FULL' >= 'VIEW' is false lexicographically.
   */
  const groups = useMemo(() => {
    const visible = MODULE_REGISTRY.filter((entry) => allows(entry.module, entry.minLevel));
    return NAV_GROUPS.map((group) => ({
      group,
      entries: visible.filter((e) => e.group === group),
      pending: PENDING_NAV.filter((p) => p.group === group),
    })).filter((s) => s.entries.length > 0 || s.pending.length > 0);
  }, [allows]);

  return (
    <aside
      className={cn(
        'flex h-full shrink-0 flex-col overflow-visible border-r border-neutral-300 bg-surface-raised',
        'transition-[width] duration-rail ease-rail',
        isCollapsed ? 'w-sidebar-rail' : 'w-sidebar',
      )}
      aria-label="Main navigation"
    >
      <div
        className={cn(
          'flex min-h-topbar items-center gap-2.5 border-b border-neutral-200 transition-[padding] duration-rail ease-rail',
          isCollapsed ? 'flex-col justify-center gap-1.5 px-0 py-2.5' : 'px-3.5',
        )}
      >
        <img src="/brand/guruji-mark.png" alt="" className="h-auto w-[26px] flex-none" />
        {!isCollapsed && (
          <div className="min-w-0">
            <div className="whitespace-nowrap text-[12.5px] font-heading uppercase leading-[1.15] tracking-[.2px]">
              Guruji Travel
              <br />
              Yatra
            </div>
          </div>
        )}
        {!asDrawer && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
            className={cn(
              'grid h-[26px] w-[26px] flex-none place-items-center rounded-[6px] border border-neutral-300',
              'bg-surface-raised text-neutral-600 hover:bg-neutral-100 hover:text-ink',
              !isCollapsed && 'ml-auto',
            )}
          >
            <Icon name="menu" size={14} />
          </button>
        )}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden px-2.5 pb-5 pt-3">
        {groups.map(({ group, entries, pending }) => (
          <NavSection
            key={group}
            group={group}
            entries={entries}
            pending={pending}
            collapsed={isCollapsed}
            badges={badges}
            onNavigate={onNavigate}
            onTip={showTip}
            onTipHide={hideTip}
            onPrefetch={prefetchModule}
          />
        ))}
      </nav>

      {/* Rail tooltip. Fixed-positioned so it escapes the sidebar's overflow. */}
      {isCollapsed && tip !== null && (
        <div
          role="tooltip"
          style={{ top: tip.top }}
          className="pointer-events-none fixed left-[82px] z-[120] -translate-y-1/2 whitespace-nowrap rounded-[6px] bg-ink px-2.5 py-1.5 text-[11.5px] font-semibold leading-none text-white shadow-menu"
        >
          {tip.label}
        </div>
      )}

      <div className="border-t border-neutral-200 px-2.5 py-3">
        <button
          type="button"
          disabled
          title={CUSTOMER_PORTAL_ENTRY.reason}
          className={cn(
            'flex w-full cursor-not-allowed items-center gap-[11px] rounded-[7px] border border-dashed border-neutral-300',
            'bg-neutral-100 px-[11px] py-2.5 text-left text-[12.5px] font-semibold leading-none text-neutral-500',
            isCollapsed && 'justify-center px-0',
          )}
        >
          <Icon name="portal" size={16} className="flex-none" />
          {!isCollapsed && <span className="whitespace-nowrap">Customer Portal</span>}
        </button>
      </div>
    </aside>
  );
}

function NavSection({
  group,
  entries,
  pending,
  collapsed,
  badges,
  onNavigate,
  onTip,
  onTipHide,
  onPrefetch,
}: {
  group: NavGroup;
  entries: readonly ModuleRegistryEntry[];
  pending: readonly PendingNavEntry[];
  collapsed: boolean;
  badges: Partial<Record<BadgeKey, number>>;
  onNavigate?: () => void;
  onTip: (label: string, top: number) => void;
  onTipHide: () => void;
  onPrefetch: (module: ModuleKey) => void;
}): JSX.Element {
  return (
    <>
      {collapsed ? (
        // Collapsed: the heading will not fit, so the grouping is carried by a
        // divider rather than dropped.
        <div className="mx-3 mb-2 mt-2.5 h-px bg-neutral-200" role="separator" aria-label={group} />
      ) : (
        <div className="whitespace-nowrap px-2.5 pb-2 pt-3.5 text-[10px] font-semibold uppercase leading-none tracking-label text-neutral-500">
          {group}
        </div>
      )}
      {entries.map((entry) => (
        <NavItem
          key={entry.path}
          entry={entry}
          collapsed={collapsed}
          badge={entry.badge !== undefined ? badges[entry.badge] : undefined}
          onNavigate={onNavigate}
          onTip={onTip}
          onTipHide={onTipHide}
          onPrefetch={onPrefetch}
        />
      ))}
      {pending.map((entry) => (
        <PendingItem
          key={entry.key}
          entry={entry}
          collapsed={collapsed}
          onTip={onTip}
          onTipHide={onTipHide}
        />
      ))}
    </>
  );
}
